import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { HandCoinsIcon as HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import BuyerConsentGate, { useBuyerConsent } from "@/components/BuyerConsentGate";

/**
 * "Make an offer" CTA for a marketplace listing. Lets a buyer propose
 * a custom price — seller gets an inbox alert.
 */
export default function MakeOfferButton({ dog, currentUserId, variant = "card" }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const consent = useBuyerConsent(dog);

  if (!currentUserId || !dog?.id || currentUserId === dog.created_by) return null;
  if (!dog.asking_price || dog.asking_price < 1) return null;

  const submit = async () => {
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      toast.error("Enter a valid offer amount");
      return;
    }
    if (!consent.ok) {
      toast.info(consent.isRestricted
        ? "Acknowledge the restricted-breed notice to send an offer."
        : "Please confirm the buyer checklist to send an offer.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/offers/dogs/${dog.id}`, {
        amount: num,
        message,
        buyer_consent: { acknowledged: true, breed_warning: dog?.breed_warning || null },
      });
      toast.success(`Offer of $${num.toFixed(2)} sent to the breeder`);
      setOpen(false);
      setAmount("");
      setMessage("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={
            variant === "card"
              ? "w-full rounded-full border-[#E5E5E5] hover:bg-[#FAFAFA]"
              : "rounded-full border-[#E5E5E5]"
          }
          data-testid={`make-offer-btn-${dog.id}`}
        >
          <HandCoins size={14} className="mr-1.5" weight="duotone" />
          Make an offer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Offer for {dog.name}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B6B6B]">
          Asking price: <strong>${Number(dog.asking_price).toFixed(2)}</strong>. Offers below 40% of asking are auto-rejected.
        </p>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-spectral">$</span>
          <input
            type="number"
            step="0.01"
            min="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Your offer"
            className="flex-1 px-3 py-2 border border-[#E5E5E5] rounded text-lg font-mono"
            data-testid={`make-offer-amount-input`}
          />
        </div>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Add a message (optional)…"
          rows={3}
          data-testid="make-offer-message-input"
        />
        <BuyerConsentGate
          listing={dog}
          actionLabel="make an offer"
          testId={`consent-gate-offer-${dog.id}`}
          onConsentChange={consent.refresh}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !amount || !consent.ok}
            className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
            data-testid="make-offer-submit-btn"
          >
            {busy ? "Sending…" : "Send offer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
